import { toastServerError } from "helpers";
import moment from "moment";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button, Loader, Modal } from "ui";

import {
  useAvailableEnrichmentProfiles,
  useEnrichProduct,
  useProductAttributes,
  useRevertProductAttributes,
  useUpdateProductAttributes
} from "../../apis";
import {
  GENERAL_PROFILE_KEY,
  PRODUCT_GENDER_LABELS,
  PRODUCT_SEASON_LABELS,
  PRODUCT_STYLE_LABELS
} from "../../constants";
import {
  EnrichmentListItem,
  EProductStyle
} from "../../recommendation.types";
import { Badge } from "../Badge";
import { ProductAttributesForm } from "../ProductAttributesForm";

import styles from "./ProductAttributesModal.module.scss";

interface ProductAttributesModalProps {
  item: EnrichmentListItem | null;
  onClose: () => void;
}

const FORM_ID = "product-attributes-form";

// The shared columns; everything else an attribute set declares is a trait.
const COLUMN_FIELDS = new Set([
  "gender",
  "itemType",
  "material",
  "style",
  "season",
  "dominantColors"
]);

const ProductAttributesModal = ({
  item,
  onClose
}: ProductAttributesModalProps) => {
  const { t } = useTranslation();

  const [isEditing, setIsEditing] = useState(false);

  const {
    attributes,
    isLoading,
    isError,
    refetch
  } = useProductAttributes(item?.productId ?? "");

  const { availableProfiles } = useAvailableEnrichmentProfiles();

  useEffect(() => {
    setIsEditing(false);
  }, [item?.productId]);

  const { mutate: enrich, isLoading: isEnriching } = useEnrichProduct({
    onSuccess: () => {
      toast.success(t("Product enriched"));
      void refetch();
    },
    onError: toastServerError
  });

  const { mutate: update, isLoading: isSaving } = useUpdateProductAttributes({
    onSuccess: () => {
      toast.success(t("Attributes saved"));
      setIsEditing(false);
      void refetch();
    },
    onError: toastServerError
  });

  const { mutate: revert, isLoading: isReverting } =
    useRevertProductAttributes({
      onSuccess: () => toast.success(t("Handed back to AI")),
      onError: toastServerError
    });

  const manualFields = new Set(attributes?.manualFields ?? []);

  const profile = availableProfiles.find(
    (candidate) =>
      candidate.key === (attributes?.profileKey ?? GENERAL_PROFILE_KEY)
  );
  const traitFields = [
    ...new Set([
      ...(profile?.fields ?? []).filter(
        (field) => !COLUMN_FIELDS.has(field)
      ),
      ...Object.keys(attributes?.traits ?? {})
    ])
  ];

  const rows = attributes
    ? [
        {
          field: "gender",
          label: t("Gender"),
          value: attributes.gender
            ? t(PRODUCT_GENDER_LABELS[attributes.gender])
            : "—"
        },
        {
          field: "itemType",
          label: t("Item type"),
          value: attributes.itemType ?? "—"
        },
        {
          field: "material",
          label: t("Material"),
          value: attributes.material ?? "—"
        },
        {
          field: "style",
          label: t("Style"),
          // A closed list since the resolver matches it by equality; anything
          // else is legacy free text, shown as it was stored.
          value: attributes.style
            ? t(
                PRODUCT_STYLE_LABELS[attributes.style as EProductStyle] ??
                  attributes.style
              )
            : "—"
        },
        {
          field: "season",
          label: t("Season"),
          value: attributes.season
            ? t(PRODUCT_SEASON_LABELS[attributes.season])
            : "—"
        },
        {
          field: null,
          label: t("Attribute set"),
          value: attributes.profileKey ?? t("General")
        }
      ]
    : [];

  // Every non-general vertical keeps its fields here — beauty today, anything
  // added later. Rendered key → value so a new vertical needs no edit. A trait
  // cleared by hand stays listed, or there would be nothing to revert.
  const traitRows = traitFields
    .map((field) => [field, attributes?.traits?.[field]] as const)
    .filter(
      ([field, value]) =>
        (value !== null && value !== undefined && value !== "") ||
        manualFields.has(`traits.${field}`)
    );

  const renderManualMark = (field: string | null) =>
    field && manualFields.has(field) ? (
      <span className={styles.attrs__manual}>
        <Badge label="Edited" color="#FB3973" />
        <button
          type="button"
          className={styles.attrs__revert}
          disabled={isReverting}
          onClick={() =>
            item && revert({ id: item.productId, fields: [field] })
          }
        >
          {t("Revert to AI")}
        </button>
      </span>
    ) : null;

  const renderForm = () => (
    <ProductAttributesForm
      formId={FORM_ID}
      attributes={attributes}
      traitFields={traitFields}
      vocabularies={profile?.vocabularies ?? {}}
      onSave={(payload) =>
        item && update({ id: item.productId, payload })
      }
      onCancel={() => setIsEditing(false)}
    />
  );

  return (
    <Modal
      isOpen={Boolean(item)}
      title={item?.name ?? t("Product attributes")}
      // Editing keeps Save and Cancel in the modal footer, clear of a long
      // attribute set scrolling underneath.
      footer={isEditing ? undefined : <></>}
      formId={FORM_ID}
      okText={t("Save")}
      isLoading={isSaving}
      onCancel={() => setIsEditing(false)}
      onClose={onClose}
    >
      <div className={styles.content}>
        {isLoading ? (
          <Loader />
        ) : isEditing ? (
          renderForm()
        ) : isError || !attributes ? (
          <div className={styles.empty}>
            <p className={styles.empty__text}>
              {t("This product has not been enriched yet.")}
            </p>
            <div className={styles.actions}>
              <Button
                loading={isEnriching}
                onClick={() => item && enrich(item.productId)}
              >
                {t("Enrich now")}
              </Button>
              <Button variant="default" onClick={() => setIsEditing(true)}>
                {t("Set by hand")}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.attrs}>
              {rows.map((row) => (
                <div key={row.label} className={styles.attrs__row}>
                  <span className={styles.attrs__label}>
                    {row.label}
                    {renderManualMark(row.field)}
                  </span>
                  <span className={styles.attrs__value}>{row.value}</span>
                </div>
              ))}
            </div>

            {traitRows.length > 0 && (
              <div className={styles.attrs}>
                {traitRows.map(([key, value]) => (
                  <div key={key} className={styles.attrs__row}>
                    <span className={styles.attrs__label}>
                      {key}
                      {renderManualMark(`traits.${key}`)}
                    </span>
                    <span className={styles.attrs__value}>
                      {value === null || value === undefined || value === ""
                        ? "—"
                        : Array.isArray(value)
                          ? value.join(", ")
                          : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {((attributes.dominantColors &&
              attributes.dominantColors.length > 0) ||
              manualFields.has("dominantColors")) && (
              <div className={styles.colors}>
                <span className={styles.attrs__label}>
                  {t("Dominant colors")}
                  {renderManualMark("dominantColors")}
                </span>
                <div className={styles.colors__chips}>
                  {(attributes.dominantColors ?? []).map((color) => (
                    <span key={color} className={styles.colors__chip}>
                      {color}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.meta}>
              {attributes.enrichmentModel && (
                <span>
                  {t("Model")}: {attributes.enrichmentModel}
                </span>
              )}
              {attributes.enrichedAt && (
                <span>
                  {t("Enriched")}{" "}
                  {moment(attributes.enrichedAt).format("DD/MM/YYYY HH:mm")}
                </span>
              )}
              {attributes.editedAt && (
                <span>
                  {t("Edited by hand")}{" "}
                  {moment(attributes.editedAt).format("DD/MM/YYYY HH:mm")}
                </span>
              )}
            </div>

            <div className={styles.actions}>
              <Button variant="default" onClick={() => setIsEditing(true)}>
                {t("Edit")}
              </Button>
              <Button
                variant="default"
                loading={isEnriching}
                onClick={() => item && enrich(item.productId)}
              >
                {t("Re-enrich")}
              </Button>
              {manualFields.size > 0 && (
                <Button
                  variant="default"
                  loading={isReverting}
                  onClick={() => item && revert({ id: item.productId })}
                >
                  {t("Revert all to AI")}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ProductAttributesModal;
